import { useEffect } from 'react'
import type Persona from 'persona'

import { logError, logInfo } from '@sputnik/utils/loggerUtils'

type PersonaClientProps = {
  inquiryId: string
  setInquiryId: (inquiryId: string | null) => void
}

const PersonaClient = ({ inquiryId, setInquiryId }: PersonaClientProps) => {
  useEffect(() => {
    let client: InstanceType<typeof Persona.Client> | undefined
    let isCancelled = false

    if (inquiryId) {
      import(/* webpackChunkName: "persona-sdk" */ 'persona')
        .then(({ default: Persona }) => {
          if (isCancelled) {
            return
          }

          client = new Persona.Client({
            inquiryId,
            environmentId: window.__DivvyEnvironment.PERSONA_ENV || 'sandbox',
            onCancel: ({ inquiryId }) => {
              logInfo('personaQueryCancelled', {
                attributes: { inquiryId, result: `${inquiryId} exit` },
                eventName: 'PersonaClientOnCancel',
              })
              setInquiryId(null)
            },
            onComplete: ({ inquiryId, status }) => {
              logInfo('personaQueryComplete', {
                attributes: { inquiryId, result: `${inquiryId} ${status}` },
                eventName: 'PersonaClientOnComplete',
              })
              setInquiryId(null)
            },
            onError: (error) => {
              console.error('Inquiry error:', error)
              logError(error, {
                attributes: {
                  action: 'personaQueryError',
                  inquiryId,
                  result: `${error.status} — ${error.code}`,
                },
                eventName: 'PersonaClientOnError',
              })
              setInquiryId(null)
            },
          })

          client.open()
        })
        .catch((error) => {
          logError(error, {
            attributes: {
              action: 'personaSdkLoadError',
              inquiryId,
              result: 'Error loading persona SDK',
            },
            eventName: 'PersonaClientOnLoadError',
          })
          setInquiryId(null)
        })
    }

    return () => {
      isCancelled = true
      client?.destroy()
    }
  }, [inquiryId, setInquiryId])

  return null
}

export default PersonaClient
